import React, { createContext, useContext, useState, ReactNode } from 'react';
import { ServiceItem, BusinessListItem, StaffMember, SelectedService } from '../types';

interface BookingState {
  business: BusinessListItem | null;
  selectedServices: SelectedService[];
  selectedStaff: StaffMember | null;
  selectedDate: Date | null;
  selectedTime: string | null;
  customerInfo: {
    name: string;
    email: string;
    phone: string;
  };
}

interface BookingContextType {
  bookingState: BookingState;
  setBusiness: (business: BusinessListItem) => void;
  addService: (service: ServiceItem) => void;
  removeService: (serviceId: string) => void;
  clearServices: () => void;
  setSelectedStaff: (staff: StaffMember | null) => void;
  setSelectedDate: (date: Date | null) => void;
  setSelectedTime: (time: string | null) => void;
  setCustomerInfo: (info: BookingState['customerInfo']) => void;
  getTotalPrice: () => number;
  getTotalDuration: () => number;
  resetBooking: () => void;
}

const initialState: BookingState = {
  business: null,
  selectedServices: [],
  selectedStaff: null,
  selectedDate: null,
  selectedTime: null,
  customerInfo: {
    name: '',
    email: '',
    phone: '',
  },
};

const BookingContext = createContext<BookingContextType | undefined>(undefined);

export const BookingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [bookingState, setBookingState] = useState<BookingState>(initialState);

  const setBusiness = (business: BusinessListItem) => {
    setBookingState(prev => ({ ...prev, business }));
  };

  const addService = (service: ServiceItem) => {
    setBookingState(prev => {
      const exists = prev.selectedServices.find(s => s.service.id === service.id);
      if (exists) return prev;
      return {
        ...prev,
        selectedServices: [...prev.selectedServices, { service }],
      };
    });
  };

  const removeService = (serviceId: string) => {
    setBookingState(prev => ({
      ...prev,
      selectedServices: prev.selectedServices.filter(s => s.service.id !== serviceId),
    }));
  };

  const clearServices = () => {
    setBookingState(prev => ({ ...prev, selectedServices: [] }));
  };

  const setSelectedStaff = (staff: StaffMember | null) => {
    setBookingState(prev => ({ ...prev, selectedStaff: staff }));
  };

  const setSelectedDate = (date: Date | null) => {
    setBookingState(prev => ({ ...prev, selectedDate: date }));
  };

  const setSelectedTime = (time: string | null) => {
    setBookingState(prev => ({ ...prev, selectedTime: time }));
  };

  const setCustomerInfo = (info: BookingState['customerInfo']) => {
    setBookingState(prev => ({ ...prev, customerInfo: info }));
  };

  const getTotalPrice = () => {
    return bookingState.selectedServices.reduce(
      (sum, s) => sum + s.service.price,
      0
    );
  };

  const getTotalDuration = () => {
    return bookingState.selectedServices.reduce(
      (sum, s) => sum + s.service.duration,
      0
    );
  };

  const resetBooking = () => {
    setBookingState(initialState);
  };

  return (
    <BookingContext.Provider
      value={{
        bookingState,
        setBusiness,
        addService,
        removeService,
        clearServices,
        setSelectedStaff,
        setSelectedDate,
        setSelectedTime,
        setCustomerInfo,
        getTotalPrice,
        getTotalDuration,
        resetBooking,
      }}
    >
      {children}
    </BookingContext.Provider>
  );
};

export const useBooking = () => {
  const context = useContext(BookingContext);
  if (context === undefined) {
    throw new Error('useBooking must be used within a BookingProvider');
  }
  return context;
};

